import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, Building2, Lock } from "lucide-react";
import { BRAND, CAR_TYPES } from "@/lib/brand";
import { Logo } from "@/components/site/SiteLayout";

const explore = [{ to: "/cars", label: "All Cars" }, ...CAR_TYPES.map((t) => ({ to: `/cars?type=${t.type}`, label: t.label }))];
const company = [
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact Us" },
  { to: "/faq", label: "FAQ" },
  { to: "/offers", label: "Offers & Coupons" },
  { to: "/support", label: "Help & Support" },
  { to: "/business", label: "Corporate Mobility" },
  { to: "/verify", label: "Driver Verification" },
  { to: "/bookings", label: "My Bookings" },
  { to: "/#get-app", label: "Android App" },
  { to: "/legal", label: "All Policies" },
];
const legal = [
  ["terms", "Terms of Use"], ["cancellation-refund", "Cancellation & Refund Policy"], ["security-deposit", "Security Deposit Policy"],
  ["cookies", "Cookie Policy"], ["grievance", "Grievance Redressal"], ["kyc-verification", "Driver Verification (KYC) Policy"], ["payments-billing", "Payments & Billing Policy"],
].map(([s, l]) => ({ to: `/legal/${s}`, label: l }));
const more = [
  ["privacy", "Privacy Policy"], ["rental-agreement", "Rental Agreement"], ["fuel-damage", "Fuel, Damage & Fines Policy"],
  ["host-terms", "Host Terms"], ["disclaimer", "Disclaimer"], ["delivery-pickup", "Vehicle Delivery & Pickup Policy"],
].map(([s, l]) => ({ to: `/legal/${s}`, label: l }));

const linkCls = "text-[13px] text-white/60 transition-colors duration-200 hover:text-hc-gold";

function Col({ title, items, prefix }) {
  return (
    <div>
      <h4 className="mb-4 text-[15px] font-semibold text-white">{title}</h4>
      <ul className="space-y-2">
        {items.map((l) => <li key={l.to}><Link className={linkCls} to={l.to} data-testid={`${prefix}-${l.to.split(/[/?=]/).pop()}`}>{l.label}</Link></li>)}
      </ul>
    </div>
  );
}

function BrandCol() {
  const row = "flex items-start gap-3 text-[13px] text-white/75 [&>svg]:!text-hc-gold [&_a:hover]:!text-hc-gold";
  return (
    <div className="col-span-2 space-y-4 md:col-span-1">
      <Logo dark />
      <p className="max-w-[270px] text-[13px] leading-5 text-white/60">Self-drive car rentals in {BRAND.city}. Clean, inspected cars, verified drivers and transparent pricing with no hidden charges.</p>
      <ul className="space-y-2">
        <li className={row}><Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#FF5A00]" /><a href={`mailto:${BRAND.email}`} data-testid="footer-email" className="transition-colors duration-200 hover:text-[#FF5A00]">{BRAND.email}</a></li>
        <li className={row}><Phone className="mt-0.5 h-4 w-4 shrink-0 text-[#FF5A00]" /><a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} data-testid="footer-phone" className="transition-colors duration-200 hover:text-[#FF5A00]">{BRAND.phone}</a></li>
        <li className={row}><Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#FF5A00]" />{BRAND.hours}</li>
        <li className={`${row} max-w-[280px]`}><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#FF5A00]" />{BRAND.address}</li>
      </ul>
    </div>
  );
}

function CompanyBar() {
  const items = [["CIN", BRAND.cin], ["GSTIN", BRAND.gstin], ["Legal Name", BRAND.legalName]];
  return (
    <div className="container-x">
      <div className="grid grid-cols-1 gap-4 rounded-xl border border-hc-gold/25 bg-white/[0.04] px-5 py-4 sm:grid-cols-2 md:grid-cols-[1.1fr_1fr_1fr_1.4fr] md:items-center md:gap-0 md:divide-x md:divide-white/15" data-testid="footer-registered-details">
        <p className="flex items-center gap-3 text-sm font-semibold text-white md:pr-4"><Building2 className="h-7 w-7 !text-hc-gold" strokeWidth={1.5} />Registered Company Details</p>
        {items.map(([k, v]) => (
          <div key={k} className="md:px-5 lg:px-8">
            <p className="text-[11px] uppercase tracking-[0.12em] text-white/45">{k}</p>
            <p className="mt-0.5 text-sm text-neutral-100" data-testid={`footer-reg-${k.toLowerCase().replace(/\s/g, "-")}`}>{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#0A2E1D] text-white/75" data-testid="site-footer">
      <div className="h-1 bg-gradient-to-r from-hc-green via-hc-gold to-hc-green" />
      <div className="container-x grid grid-cols-2 gap-x-6 gap-y-10 py-12 md:grid-cols-[1.45fr_0.85fr_0.95fr_1.2fr_1.2fr] md:gap-6 lg:gap-8">
        <BrandCol />
        <Col title="Explore" items={explore} prefix="footer-explore" />
        <Col title="Company" items={company} prefix="footer-company" />
        <Col title="Legal & Policies" items={legal} prefix="footer-legal" />
        <Col title="More Policies" items={more} prefix="footer-legal" />
      </div>
      <CompanyBar />
      <div className="container-x grid gap-3 pb-20 pt-6 text-xs text-white/55 md:grid-cols-3 md:items-center md:pb-6">
        <p>© 2026 {BRAND.legalName}. All rights reserved.<br />{BRAND.name} is a brand of {BRAND.legalName}.</p>
        <p className="flex items-center gap-2 md:justify-center"><Lock className="h-4 w-4 text-hc-gold" />Secure payments by Cashfree · UPI · Cards · Netbanking</p>
        <p className="md:text-right" data-testid="footer-developer">Website developed by{" "}
          <a href={BRAND.developer.url} target="_blank" rel="noopener noreferrer" data-testid="footer-developer-link" className="font-semibold text-white transition-colors duration-200 hover:text-hc-gold">{BRAND.developer.name}</a>
        </p>
      </div>
    </footer>
  );
}
